import React, { useState } from 'react';

import { useIsMutating, useQuery } from '@tanstack/react-query';
import AddIcon from 'icons/add.svg';
import ReorderIcon from 'icons/low_priority.svg';
import PageWithContentIcon from 'icons/page-with-content.svg';
import ProcoreLogo from 'icons/procore_logo.svg';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import useAttachmentsUploader from 'components/hooks/useAttachmentsUploader';
import usePdfMutations from 'components/hooks/usePdfMutations';

import OutlinedButton from 'components/application/buttons/OutlinedButton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectDocumentsContext } from 'components/contexts/ProjectDocumentsContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import AdditionalProjectDocumentsRow from 'components/projectDocuments/components/AdditionalProjectDocumentsRow';
import DefaultProjectDocumentsRow from 'components/projectDocuments/components/DefaultProjectDocumentsRow';
import DestroyProjectDocumentModal from 'components/projectDocuments/components/DestroyProjectDocumentModal';
import ReorderDocumentsModal from 'components/projectDocuments/components/ReorderDocumentsModal';
import ValidationErrorMessage from 'components/uploader/components/ValidationErrorMessage';

import { formatDate } from 'components/helpers/dates';
import { DEFAULT_MAX_SIZE, mimeTypes } from 'components/helpers/files';

const acceptedMimeTypes = mimeTypes({ fileTypes: ['PDF'] });
const maxSize = DEFAULT_MAX_SIZE;

export default function ProjectDocumentsTab() {
  const breadBoard = useBreadBoard();

  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const isMutating = useIsMutating() > 0;

  const {
    procoreProjectIntegration,
    projectId,
    projectPolicy,
    riskAssessmentOnlyProjectPolicy,
  } = useProjectDocumentsContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectDocuments.components.ProjectDocumentsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    createPdfMutation,
    deletePdfMutation,
    updateCoverSheetSelectionMutation,
    updatePdfPositionMutation,
  } = usePdfMutations({ projectId });

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [selectedPdfId, setSelectedPdfId] = useState(null);

  const { data: domainProjectPdfs, isSuccess: isDomainProjectPdfsSuccess } =
    useQuery({
      queryKey: ['projectPdfs', projectId],
      queryFn: async () => {
        const response = await axios.get(`/projects/${projectId}/pdfs`);
        return response.data;
      },
      refetchInterval: (data) =>
        data?.meta?.defaultPdfsPending ? 1000 : false,
    });

  const handleUpdateCoverSheetSelection = ({ pdfId, selected }) =>
    updateCoverSheetSelectionMutation.mutate(
      { pdfId, selected },
      {
        onSuccess: () =>
          window.location.replace(
            `/projects/${projectId}?switch_tab=documents-tab`,
          ),
        onError: breadBoard.addInedibleToast,
      },
    );

  const handlePdfPositionOrderSubmit = (reorderedPdfIds) =>
    updatePdfPositionMutation.mutate(
      {
        pdf_position_configuration: {
          pdf_attributes: Object.fromEntries(
            reorderedPdfIds.map((pdfId, index) => [
              pdfId,
              { position: index + 1 },
            ]),
          ),
        },
      },
      {
        onSuccess: () =>
          window.location.replace(
            `/projects/${projectId}?switch_tab=documents-tab`,
          ),
        onError: breadBoard.addInedibleToast,
        onSettled: () => setIsReorderModalOpen(false),
      },
    );

  const { handleFilesSelect } = useAttachmentsUploader({
    type: 'pdf',
    onUploaderMutationSuccess: (uploadKey) =>
      createPdfMutation.mutateAsync(uploadKey, {
        onSuccess: () =>
          window.location.replace(
            `/projects/${projectId}?switch_tab=documents-tab`,
          ),
      }),
    onUploaderMutationError: breadBoard.addInedibleToast,
  });

  const handleFilesRejection = (files) => {
    breadBoard.addInedibleToast({
      fullMessage: (
        <ValidationErrorMessage
          acceptedMimeTypes={acceptedMimeTypes}
          file={files[0]}
          maxSize={maxSize}
        />
      ),
    });
  };

  const handleDeletePdfSubmit = () =>
    deletePdfMutation.mutate(selectedPdfId, {
      onSuccess: () =>
        window.location.replace(
          `/projects/${projectId}?switch_tab=documents-tab`,
        ),
      onError: breadBoard.addInedibleToast,
      onSettled: () => {
        setSelectedPdfId(null);
        setIsDestroyModalOpen(false);
      },
    });

  const handleDeleteClick = (pdfId) => {
    setSelectedPdfId(pdfId);
    setIsDestroyModalOpen(true);
  };

  const projectPdfCount = domainProjectPdfs?.data?.length;
  const shouldShowCoversheet = projectPdfCount > 0 && projectPolicy.canUpdate;

  const shouldBlockMutations =
    isMutating ||
    !isDomainProjectPdfsSuccess ||
    domainProjectPdfs?.meta?.defaultPdfsPending;

  const { publishedAt, version } =
    procoreProjectIntegration?.data?.attributes || {};

  return (
    <>
      {isReorderModalOpen && (
        <ReorderDocumentsModal
          isUpdatePdfPositionMutationLoading={
            updatePdfPositionMutation.isLoading
          }
          onClose={() => setIsReorderModalOpen(false)}
          onSave={handlePdfPositionOrderSubmit}
          projectPdfs={domainProjectPdfs?.data}
        />
      )}
      {isDestroyModalOpen && (
        <DestroyProjectDocumentModal
          isDeletePdfMutationLoading={deletePdfMutation.isLoading}
          onClose={() => setIsDestroyModalOpen(false)}
          onDestroySubmit={handleDeletePdfSubmit}
        />
      )}
      {projectPolicy.canUpdate && (
        <div className='tw-border-l tw-border-r tw-flex tw-h-14 tw-min-h-14 tw-w-full tw-min-w-full tw-items-center tw-justify-end tw-border-0 tw-border-l-1 tw-border-r-1 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-5'>
          {publishedAt && version && (
            <div className='tw-mr-auto tw-flex tw-h-8 tw-items-center'>
              <ProcoreLogo className='tw-h-4 tw-w-4' />
              <span className='tw-ml-2.5'>
                {i18nT('procoreVersionPublished', {
                  interpolation: { escapeValue: false },
                  publishedDate: formatDate({
                    date: publishedAt,
                    format: 'dd MMM yyyy',
                  }),
                  versionNumber: version,
                })}
              </span>
            </div>
          )}
          <div className='tw-mr-4'>
            <OutlinedButton
              color='grey'
              disabled={shouldBlockMutations || projectPdfCount < 2}
              onClick={() => setIsReorderModalOpen(true)}
              size='sm'
            >
              <ReorderIcon className='-tw-ml-2' height={24} width={24} />
              <span>{i18nT('reorderDocuments')}</span>
            </OutlinedButton>
          </div>
          <PdfUploadButton
            isDisabled={shouldBlockMutations}
            onFileSelect={(acceptedFiles, fileRejections) => {
              if (fileRejections.length) {
                handleFilesRejection(fileRejections);
              } else {
                handleFilesSelect(acceptedFiles);
              }
            }}
          />
        </div>
      )}
      {!projectPolicy.canUpdate && projectPdfCount === 0 ?
        <section className='section section--notice section--notice--no-top-border-radius tw-border-grey-100 tw-bg-white tw-px-20'>
          <PageWithContentIcon
            className='[&_path]:tw-fill-grey-500'
            height={42}
            width={42}
          />
          <h2 className='section__header tw-pt-0 tw-text-l tw-font-semibold tw-text-grey-900'>
            {i18nT('emptyState.title')}
          </h2>
          <p className='section__content tw-mb-0 tw-mt-2'>
            {i18nT('emptyState.description', {
              interpolation: { escapeValue: false },
              singularRAMSName,
            })}
          </p>
        </section>
      : <table className='table handshq__table tw-border-grey-100 tw-bg-white'>
          <thead className='handshq__thead'>
            <tr className='tw-bg-grey-050'>
              <th className='tw-pl-5 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('shared.document')}
              </th>
              <th className='tw-w-36 tw-text-center tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('shared.actions')}
              </th>
              {shouldShowCoversheet && (
                <th className='tw-w-30 tw-text-center tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                  {i18nTCommon('projects.coverSheet')}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {projectPolicy.canUpdate && (
              <>
                <DefaultProjectDocumentsRow
                  documentName={i18nTCommon('projects.riskAssessment')}
                  href={`/projects/${projectId}/risk_assessment`}
                  shouldShowCoversheet={shouldShowCoversheet}
                />
                {riskAssessmentOnlyProjectPolicy.canEdit && (
                  <>
                    <DefaultProjectDocumentsRow
                      documentName={i18nTCommon(
                        'projects.sequenceOfOperations',
                      )}
                      href={`/projects/${projectId}/sequence_of_operations`}
                      shouldShowCoversheet={shouldShowCoversheet}
                    />
                    <DefaultProjectDocumentsRow
                      documentName={i18nTCommon('projects.methodStatement')}
                      href={`/projects/${projectId}/method`}
                      shouldShowCoversheet={shouldShowCoversheet}
                    />
                  </>
                )}
              </>
            )}
            {(
              domainProjectPdfs?.meta?.defaultPdfsPending &&
              projectPdfCount === 0
            ) ?
              <tr className='hover:tw-bg-grey-025'>
                <td
                  className='tw-border-grey-100 tw-text-center tw-align-middle tw-font-medium tw-text-grey-900'
                  colSpan={2}
                >
                  {i18nT('defaultPdfsPending', {
                    interpolation: { escapeValue: false },
                    singularRAMSName,
                  })}
                </td>
              </tr>
            : domainProjectPdfs?.data?.map((pdf) => (
                <AdditionalProjectDocumentsRow
                  key={pdf.id}
                  onDeleteClick={handleDeleteClick}
                  onUpdateCoverSheetSelection={handleUpdateCoverSheetSelection}
                  pdf={pdf}
                  shouldBlockMutations={shouldBlockMutations}
                />
              ))
            }
          </tbody>
        </table>
      }
    </>
  );
}

function PdfUploadButton({ isDisabled, onFileSelect }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectDocuments.components.ProjectDocumentsTab.PdfUploadButton',
  });

  const { getInputProps, getRootProps, open } = useDropzone({
    accept: acceptedMimeTypes,
    multiple: false,
    maxSize,
    maxUploads: 1,
    onDrop: onFileSelect,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <OutlinedButton
        className='tw-apply-outlined-button tw-apply-outlined-button--on-light tw-apply-outlined-button--sm tw-apply-outlined-button--grey'
        disabled={isDisabled}
        onClick={open}
        type='button'
      >
        <AddIcon className='-tw-ml-2' height={24} width={24} />
        {i18nT('button')}
      </OutlinedButton>
    </div>
  );
}

PdfUploadButton.propTypes = {
  isDisabled: PropTypes.bool,
  onFileSelect: PropTypes.func.isRequired,
};
